import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, Percent, Edit2, Calendar, Loader2, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "today", label: "Hoy" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
  { key: "year", label: "Año" },
  { key: "all", label: "Todo" },
];

const STATUS_LABELS = {
  received: "Recibido",
  in_progress: "En Proceso",
  shipped: "Enviado",
  delivered: "Entregado",
};

const STATUS_COLORS = {
  received: "bg-stone-100 text-stone-700",
  in_progress: "bg-amber-100 text-amber-700",
  shipped: "bg-emerald-100 text-emerald-700",
  delivered: "bg-blue-100 text-blue-700",
};

const currency = (n) => `$${(n || 0).toFixed(2)}`;

const FinancialsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [period, setPeriod] = useState("month");
  const [data, setData] = useState({ rows: [], totals: { count: 0, revenue: 0, cost: 0, profit: 0, margin: 0 }, timeseries: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Edit dialog
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    revenue: "",
    cost_materials: "",
    cost_production: "",
    cost_logistics: "",
    cost_financial: "",
    cost_advertising: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/financial-summary?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      toast.error("Error al cargar reporte financiero");
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (token) fetchData(); }, [token, period]);

  const filtered = useMemo(() => {
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter(
      (r) => r.tracking_number?.toLowerCase().includes(q) || r.client_name?.toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      revenue: String(row.revenue || ""),
      cost_materials: String(row.cost_materials || ""),
      cost_production: String(row.cost_production || ""),
      cost_logistics: String(row.cost_logistics || ""),
      cost_financial: String(row.cost_financial || ""),
      cost_advertising: String(row.cost_advertising || ""),
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/orders/${editing.id}/financials`,
        {
          revenue: parseFloat(editForm.revenue) || 0,
          cost_materials: parseFloat(editForm.cost_materials) || 0,
          cost_production: parseFloat(editForm.cost_production) || 0,
          cost_logistics: parseFloat(editForm.cost_logistics) || 0,
          cost_financial: parseFloat(editForm.cost_financial) || 0,
          cost_advertising: parseFloat(editForm.cost_advertising) || 0,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Finanzas actualizadas");
      setEditing(null);
      fetchData();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const totals = data.totals || {};

  return (
    <div className="min-h-screen bg-stone-50" data-testid="financials-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="financials-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Admin
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" /> Reporte Financiero
              </h1>
              <p className="text-xs text-stone-500">Gastos y ganancias por pedido</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Period filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <Calendar className="w-4 h-4 text-stone-500" />
          <span className="text-sm text-stone-600 mr-2">Período:</span>
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={period === p.key ? "default" : "outline"}
              onClick={() => setPeriod(p.key)}
              className={period === p.key ? "bg-emerald-600 hover:bg-emerald-700" : ""}
              data-testid={`financials-period-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
        </div>

        {/* Totals cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="financials-total-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="p-2 bg-blue-100 rounded-lg"><DollarSign className="w-5 h-5 text-blue-600" /></div>
                <span className="text-xs text-stone-500">{totals.count || 0} pedidos</span>
              </div>
              <p className="text-xs text-stone-500 mt-3">Ingresos</p>
              <p className="text-2xl font-bold text-stone-900">{currency(totals.revenue)}</p>
            </CardContent>
          </Card>
          <Card data-testid="financials-total-cost">
            <CardContent className="p-5">
              <div className="p-2 bg-red-100 rounded-lg inline-block"><TrendingDown className="w-5 h-5 text-red-600" /></div>
              <p className="text-xs text-stone-500 mt-3">Gastos</p>
              <p className="text-2xl font-bold text-stone-900">{currency(totals.cost)}</p>
            </CardContent>
          </Card>
          <Card data-testid="financials-total-profit">
            <CardContent className="p-5">
              <div className="p-2 bg-emerald-100 rounded-lg inline-block"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
              <p className="text-xs text-stone-500 mt-3">Ganancia</p>
              <p className={`text-2xl font-bold ${totals.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                {currency(totals.profit)}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="financials-total-margin">
            <CardContent className="p-5">
              <div className="p-2 bg-violet-100 rounded-lg inline-block"><Percent className="w-5 h-5 text-violet-600" /></div>
              <p className="text-xs text-stone-500 mt-3">Margen</p>
              <p className={`text-2xl font-bold ${(totals.margin || 0) >= 0 ? "text-violet-700" : "text-red-700"}`}>
                {(totals.margin || 0).toFixed(1)}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Cost breakdown */}
        <Card data-testid="financials-breakdown">
          <CardContent className="p-5">
            <p className="text-xs font-semibold text-stone-600 uppercase mb-3">Desglose de Gastos</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { key: "cost_materials", label: "Materiales", icon: "📦", color: "amber" },
                { key: "cost_production", label: "Producción", icon: "⚙️", color: "orange" },
                { key: "cost_logistics", label: "Logística", icon: "🚚", color: "red" },
                { key: "cost_financial", label: "Fees", icon: "💳", color: "rose" },
                { key: "cost_advertising", label: "Publicidad", icon: "📢", color: "pink" },
              ].map(({ key, label, icon, color }) => {
                const val = totals[key] || 0;
                const pct = totals.cost > 0 ? (val / totals.cost * 100) : 0;
                const colorMap = {
                  amber: "bg-amber-50 border-amber-200 text-amber-700",
                  orange: "bg-orange-50 border-orange-200 text-orange-700",
                  red: "bg-red-50 border-red-200 text-red-700",
                  rose: "bg-rose-50 border-rose-200 text-rose-700",
                  pink: "bg-pink-50 border-pink-200 text-pink-700",
                };
                return (
                  <div key={key} className={`rounded-xl border p-4 ${colorMap[color]}`} data-testid={`breakdown-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xl">{icon}</span>
                      <span className="text-xs font-medium">{pct.toFixed(0)}%</span>
                    </div>
                    <p className="text-xs text-stone-600 font-medium">{label}</p>
                    <p className="text-xl font-bold">{currency(val)}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Line chart */}
        <Card data-testid="financials-chart-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <LineIcon className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-stone-800">
                Evolución: Ingreso vs Gasto vs Ganancia
              </h3>
              <span className="text-xs text-stone-400 ml-auto">
                {data.timeseries?.length || 0} punto(s)
              </span>
            </div>
            {isLoading ? (
              <div className="h-[260px] flex items-center justify-center text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : !data.timeseries || data.timeseries.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-stone-400 text-sm">
                No hay datos para este período
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.timeseries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e4" />
                  <XAxis dataKey="label" tick={{ fill: "#78716c", fontSize: 12 }} />
                  <YAxis
                    tick={{ fill: "#78716c", fontSize: 12 }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    formatter={(v) => `$${Number(v).toFixed(2)}`}
                    contentStyle={{ borderRadius: 8, border: "1px solid #e7e5e4", fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="revenue" name="Ingreso" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cost" name="Gasto" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="profit" name="Ganancia" stroke="#059669" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Search */}
        <Input
          placeholder="Buscar por tracking # o cliente..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-white max-w-md"
          data-testid="financials-search"
        />

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {isLoading ? (
              <div className="p-10 text-center text-stone-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando...</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-stone-400">No hay pedidos en este período</div>
            ) : (
              <table className="w-full" data-testid="financials-table">
                <thead className="bg-stone-100 text-xs uppercase text-stone-600">
                  <tr>
                    <th className="text-left px-4 py-3">Tracking #</th>
                    <th className="text-left px-4 py-3">Cliente</th>
                    <th className="text-left px-4 py-3">Fecha</th>
                    <th className="text-left px-4 py-3">Estado</th>
                    <th className="text-right px-4 py-3">Ingreso</th>
                    <th className="text-right px-4 py-3">Gasto</th>
                    <th className="text-right px-4 py-3">Ganancia</th>
                    <th className="text-right px-4 py-3">Margen</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100 text-sm">
                  {filtered.map((row) => (
                    <tr key={row.id} className="hover:bg-stone-50" data-testid={`financials-row-${row.tracking_number}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-indigo-600">{row.tracking_number}</td>
                      <td className="px-4 py-3 text-stone-800">{row.client_name}</td>
                      <td className="px-4 py-3 text-stone-500">{row.created_at ? new Date(row.created_at).toLocaleDateString() : "-"}</td>
                      <td className="px-4 py-3">
                        <Badge className={STATUS_COLORS[row.status] || "bg-stone-100 text-stone-700"}>{STATUS_LABELS[row.status] || row.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-blue-700">{currency(row.revenue)}</td>
                      <td className="px-4 py-3 text-right font-medium text-red-700">{currency(row.cost)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${row.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                        {currency(row.profit)}
                      </td>
                      <td className={`px-4 py-3 text-right ${row.margin >= 0 ? "text-violet-700" : "text-red-700"}`}>
                        {(row.margin || 0).toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(row)}
                          data-testid={`financials-edit-${row.tracking_number}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="financials-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar Finanzas</DialogTitle>
          </DialogHeader>
          {editing && (() => {
            const m = parseFloat(editForm.cost_materials) || 0;
            const p = parseFloat(editForm.cost_production) || 0;
            const l = parseFloat(editForm.cost_logistics) || 0;
            const f = parseFloat(editForm.cost_financial) || 0;
            const a = parseFloat(editForm.cost_advertising) || 0;
            const totalCost = m + p + l + f + a;
            const rev = parseFloat(editForm.revenue) || 0;
            const profit = rev - totalCost;
            return (
              <div className="space-y-4 py-2">
                <div className="text-sm text-stone-500">
                  Pedido <span className="font-mono text-indigo-600 font-semibold">{editing.tracking_number}</span> &mdash; {editing.client_name}
                </div>
                <div className="space-y-2">
                  <Label className="text-blue-700">💰 Ingreso ($)</Label>
                  <Input type="number" step="0.01" value={editForm.revenue}
                    onChange={(e) => setEditForm((pp) => ({ ...pp, revenue: e.target.value }))}
                    data-testid="financials-edit-revenue" />
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-stone-600 uppercase mb-3">Desglose de Gastos</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-amber-700">📦 Materiales</Label>
                      <Input type="number" step="0.01" value={editForm.cost_materials}
                        onChange={(e) => setEditForm((pp) => ({ ...pp, cost_materials: e.target.value }))}
                        placeholder="0.00"
                        data-testid="financials-edit-materials" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-orange-700">⚙️ Producción</Label>
                      <Input type="number" step="0.01" value={editForm.cost_production}
                        onChange={(e) => setEditForm((pp) => ({ ...pp, cost_production: e.target.value }))}
                        placeholder="0.00"
                        data-testid="financials-edit-production" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-red-700">🚚 Logística</Label>
                      <Input type="number" step="0.01" value={editForm.cost_logistics}
                        onChange={(e) => setEditForm((pp) => ({ ...pp, cost_logistics: e.target.value }))}
                        placeholder="0.00"
                        data-testid="financials-edit-logistics" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-rose-700">💳 Fees</Label>
                      <Input type="number" step="0.01" value={editForm.cost_financial}
                        onChange={(e) => setEditForm((pp) => ({ ...pp, cost_financial: e.target.value }))}
                        placeholder="0.00"
                        data-testid="financials-edit-financial" />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label className="text-xs text-pink-700">📢 Publicidad</Label>
                      <Input type="number" step="0.01" value={editForm.cost_advertising}
                        onChange={(e) => setEditForm((pp) => ({ ...pp, cost_advertising: e.target.value }))}
                        placeholder="0.00"
                        data-testid="financials-edit-advertising" />
                    </div>
                  </div>
                </div>
                <div className="bg-stone-50 rounded p-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-stone-500">Gasto total</span><span className="font-bold text-red-700">{currency(totalCost)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Ganancia</span><span className={`font-bold ${profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{currency(profit)}</span></div>
                  <div className="flex justify-between"><span className="text-stone-500">Margen</span><span className="font-bold text-violet-700">{rev > 0 ? ((profit / rev) * 100).toFixed(1) : "0.0"}%</span></div>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="financials-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancialsPage;
